import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import CustomTextInput from 'components/Forms/CustomTextInput';
import usePrepTimeWebsite from 'pages/Providers/ProviderPrepTime/hooks/usePrepTimeWebsite';
import {
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
} from '@mui/material';
import PickUpBufferForProvider from 'pages/Providers/ProviderPrepTime/PickUpBufferForProvider';
import DeliveryBufferForProvider from 'pages/Providers/ProviderPrepTime/DeliveryBufferForProvider';
import {
  isAsapSwitcherTurnOff,
  isAsapSwitcherTurnOn,
} from 'redesign/BusinessTab/BusinessPrepTime/config';

const ALLOW_ASAP = 'allowAsap';
const SCHEDULED_ONLY = 'scheduledOnly';

const PrepTimeWebsite = ({ providerPrepTime }) => {
  const { t } = useTranslation();
  const {
    prepTimeMethod,
    handleValidSubmit,
    handleChangeAsap,
    handleDeliveryOrderBufferTime,
    handlePickUpOrderBufferTime,
    loading,
    disabled,
  } = usePrepTimeWebsite(providerPrepTime);

  // watch, not getValues, so selecting a radio re-renders and moves the
  // enabled/disabled state onto the other dropdown.
  const scheduledOnly = !!prepTimeMethod.watch('asap.disableAsap');

  return (
    <FormProvider {...prepTimeMethod}>
      <form onSubmit={prepTimeMethod.handleSubmit(handleValidSubmit)}>
        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12} sx={{ pl: 2 }}>
            <RadioGroup
              value={scheduledOnly ? SCHEDULED_ONLY : ALLOW_ASAP}
              onChange={(event) =>
                handleChangeAsap(event.target.value === SCHEDULED_ONLY)
              }
            >
              <FormControlLabel
                sx={{ width: 'max-content' }}
                value={ALLOW_ASAP}
                control={<Radio />}
                label={t('settings.allow_asap_orders')}
              />
              <CustomTextInput
                sx={{ width: '100%', maxWidth: '280px', mt: 1, mb: 2, ml: 4 }}
                select={true}
                name="prepTime"
                disabled={scheduledOnly}
                label={t('settings.preparation_time')}
              >
                {isAsapSwitcherTurnOff.map((option) => {
                  return (
                    <MenuItem key={option.key} value={option.value}>
                      {option.name}
                    </MenuItem>
                  );
                })}
              </CustomTextInput>

              <FormControlLabel
                sx={{ width: 'max-content' }}
                value={SCHEDULED_ONLY}
                control={<Radio />}
                label={t('settings.scheduled_orders_only')}
              />
              <CustomTextInput
                sx={{ width: '100%', maxWidth: '280px', mt: 1, mb: 2, ml: 4 }}
                select={true}
                name={'asap.qty'}
                disabled={!scheduledOnly}
                label={t('settings.minimum_advance_notice')}
              >
                {isAsapSwitcherTurnOn.map((option) => {
                  return (
                    <MenuItem key={option.key} value={option.value}>
                      {option.name}
                    </MenuItem>
                  );
                })}
              </CustomTextInput>
            </RadioGroup>
          </Grid>
          <Grid item xs={12} sx={{ pt: '8px !important', pb: 0 }}>
            <Divider sx={{ width: '100%', height: '1px' }} />
          </Grid>
          <DeliveryBufferForProvider
            prepTimeMethod={prepTimeMethod}
            handleDeliveryOrderBufferTime={handleDeliveryOrderBufferTime}
          />
          <Grid item xs={12} sx={{ pt: '8px !important', pb: 0, mt: 1.5 }}>
            <Divider sx={{ width: '100%', height: '1px' }} />
          </Grid>
          <PickUpBufferForProvider
            prepTimeMethod={prepTimeMethod}
            handlePickUpOrderBufferTime={handlePickUpOrderBufferTime}
          />
          <Grid
            item
            xs={12}
            sx={{ mr: 3, mt: 3, mb: 2, textAlign: 'end', width: '100%' }}
          >
            <LoadingButton
              variant="contained"
              type="submit"
              loading={loading}
              disabled={loading || disabled}
            >
              {t('Save')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default memo(PrepTimeWebsite);
